import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState, type FormEvent } from 'react';
import { api, errorMessage } from '../lib/api';
import { PERSON_TYPE_LABEL } from '../lib/format';
import { invalidateInventory } from '../lib/hooks';
import type { Person, PersonType } from '../lib/types';
import { useToast } from './toast';
import { Alert, Button, Field, Input, Modal, Select, Textarea } from './ui';

const empty = { type: 'STUDENT', status: 'ACTIVE', firstName: '', lastName: '', preferredName: '', email: '', hostId: '', gradYear: '', gradeLevel: '', notes: '' };

export function PersonFormModal({
  open,
  onClose,
  person,
  onSaved,
}: {
  open: boolean;
  onClose(): void;
  person?: Person | null;
  onSaved?(person: Person): void;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [form, setForm] = useState(empty);
  const [error, setError] = useState('');
  const synced = !!person && person.source !== 'MANUAL';

  useEffect(() => {
    if (!open) return;
    setError('');
    setForm(
      person
        ? {
            type: person.type,
            status: person.status,
            firstName: person.firstName,
            lastName: person.lastName,
            preferredName: person.preferredName ?? '',
            email: person.email ?? '',
            hostId: person.hostId ?? '',
            gradYear: person.gradYear ? String(person.gradYear) : '',
            gradeLevel: person.gradeLevel ?? '',
            notes: person.notes ?? '',
          }
        : empty,
    );
  }, [open, person]);

  const set = (key: keyof typeof empty) => (event: { target: { value: string } }) => setForm((f) => ({ ...f, [key]: event.target.value }));

  const mutation = useMutation({
    mutationFn: () => {
      const body = synced ? { notes: form.notes } : form;
      return person ? api.patch<Person>(`/people/${person.id}`, body) : api.post<Person>('/people', body);
    },
    onSuccess: (saved) => {
      invalidateInventory(queryClient);
      toast.success(person ? 'Saved' : `Added ${saved.firstName} ${saved.lastName}`);
      onSaved?.(saved);
      onClose();
    },
    onError: (err) => setError(errorMessage(err)),
  });

  const submit = (event: FormEvent) => {
    event.preventDefault();
    mutation.mutate();
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={person ? `Edit ${person.firstName} ${person.lastName}` : 'Add a person'}
      description={person ? undefined : 'For people who aren’t in Blackbaud - contractors, guests, or schools without a roster sync.'}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" type="submit" form="person-form" loading={mutation.isPending}>
            {person ? 'Save' : 'Add person'}
          </Button>
        </>
      }
    >
      <form id="person-form" onSubmit={submit} className="space-y-4">
        {error && <Alert tone="red">{error}</Alert>}
        {synced && (
          <Alert tone="blue">This record is synced from Blackbaud. Change profile details in Blackbaud; you can add local notes here.</Alert>
        )}
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Type" required>
            <Select value={form.type} onChange={set('type')} disabled={synced}>
              {(Object.keys(PERSON_TYPE_LABEL) as PersonType[]).map((type) => (
                <option key={type} value={type}>
                  {PERSON_TYPE_LABEL[type]}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Status">
            <Select value={form.status} onChange={set('status')} disabled={synced}>
              <option value="ACTIVE">Active</option>
              <option value="INACTIVE">Inactive</option>
            </Select>
          </Field>
          <Field label="First name" required>
            <Input value={form.firstName} onChange={set('firstName')} required disabled={synced} maxLength={100} />
          </Field>
          <Field label="Last name" required>
            <Input value={form.lastName} onChange={set('lastName')} required disabled={synced} maxLength={100} />
          </Field>
          <Field label="Preferred name">
            <Input value={form.preferredName} onChange={set('preferredName')} disabled={synced} maxLength={100} />
          </Field>
          <Field label="Email">
            <Input type="email" value={form.email} onChange={set('email')} disabled={synced} />
          </Field>
          <Field label="ID number">
            <Input value={form.hostId} onChange={set('hostId')} disabled={synced} maxLength={64} />
          </Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Grad year">
              <Input inputMode="numeric" value={form.gradYear} onChange={set('gradYear')} disabled={synced} />
            </Field>
            <Field label="Grade">
              <Input value={form.gradeLevel} onChange={set('gradeLevel')} disabled={synced} maxLength={40} />
            </Field>
          </div>
        </div>
        <Field label="Notes">
          <Textarea value={form.notes} onChange={set('notes')} maxLength={5000} />
        </Field>
      </form>
    </Modal>
  );
}
